/**
 * WebAuthn Browser API helper for biometric authentication
 */

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function base64urlToBuffer(base64url) {
  let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4) base64 += "=";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

export function isWebAuthnSupported() {
  return !!(
    window.PublicKeyCredential &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function"
  );
}

export async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

function getAuthHeaders() {
  const token = localStorage.getItem("session_token");
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

/**
 * Register a new biometric credential for the logged-in user
 */
export async function registerBiometric(credentialName = "Biometric Login") {
  // 1. Get registration options from server
  const optionsResp = await fetch(`${API}/auth/webauthn/register-options`, {
    method: "POST",
    headers: getAuthHeaders(),
    credentials: "include",
  });
  if (!optionsResp.ok) {
    const err = await optionsResp.json();
    throw new Error(err.detail || "Failed to get registration options");
  }
  const options = await optionsResp.json();

  // 2. Convert base64url fields to ArrayBuffers for the browser API
  const publicKeyOptions = {
    challenge: base64urlToBuffer(options.challenge),
    rp: options.rp,
    user: {
      id: base64urlToBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    pubKeyCredParams: options.pubKeyCredParams,
    timeout: options.timeout,
    authenticatorSelection: options.authenticatorSelection,
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({
      type: c.type,
      id: base64urlToBuffer(c.id),
    })),
    attestation: options.attestation || "none",
  };

  // 3. Prompt the user for biometric (browser handles this)
  const credential = await navigator.credentials.create({
    publicKey: publicKeyOptions,
  });

  // 4. Send the credential back to the server
  const credentialData = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      attestationObject: bufferToBase64url(credential.response.attestationObject),
    },
  };

  const verifyResp = await fetch(`${API}/auth/webauthn/register-complete`, {
    method: "POST",
    headers: getAuthHeaders(),
    credentials: "include",
    body: JSON.stringify({
      challenge_id: options.challenge_id,
      credential: credentialData,
      name: credentialName,
    }),
  });

  if (!verifyResp.ok) {
    const err = await verifyResp.json();
    throw new Error(err.detail || "Registration verification failed");
  }

  return await verifyResp.json();
}

/**
 * Authenticate using a biometric credential
 */
export async function authenticateWithBiometric(email = "") {
  // 1. Get authentication options
  const optionsResp = await fetch(`${API}/auth/webauthn/login-options`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });
  if (!optionsResp.ok) {
    const err = await optionsResp.json();
    throw new Error(err.detail || "Failed to get login options");
  }
  const options = await optionsResp.json();

  // 2. Convert for browser API
  const publicKeyOptions = {
    challenge: base64urlToBuffer(options.challenge),
    timeout: options.timeout,
    rpId: options.rpId,
    userVerification: options.userVerification,
    allowCredentials: (options.allowCredentials || []).map((c) => ({
      type: c.type,
      id: base64urlToBuffer(c.id),
    })),
  };

  // 3. Prompt for biometric
  const assertion = await navigator.credentials.get({
    publicKey: publicKeyOptions,
  });

  // 4. Send assertion to server
  const assertionData = {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
      signature: bufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? bufferToBase64url(assertion.response.userHandle)
        : null,
    },
  };

  const verifyResp = await fetch(`${API}/auth/webauthn/login-complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      challenge_id: options.challenge_id,
      credential: assertionData,
    }),
  });

  if (!verifyResp.ok) {
    const err = await verifyResp.json();
    throw new Error(err.detail || "Biometric login failed");
  }

  return await verifyResp.json();
}

/**
 * Check if biometric login is available for an email
 */
export async function checkBiometricAvailable(email) {
  try {
    const resp = await fetch(`${API}/auth/webauthn/check?email=${encodeURIComponent(email)}`);
    if (!resp.ok) return false;
    const data = await resp.json();
    return data.available;
  } catch {
    return false;
  }
}

/**
 * List registered credentials
 */
export async function listCredentials() {
  const resp = await fetch(`${API}/auth/webauthn/credentials`, {
    headers: getAuthHeaders(),
    credentials: "include",
  });
  if (!resp.ok) return [];
  const data = await resp.json();
  return data.credentials || [];
}

/**
 * Remove a credential
 */
export async function removeCredential(credentialId) {
  const resp = await fetch(`${API}/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    credentials: "include",
  });
  return resp.ok;
}
